import * as React from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { SignaturePad } from '@/signing/SignaturePad'
import { fieldValuePresent } from '@lib/helpers/field-value'
import type { SignatureValueDto } from '@lib/dto/envelope.dto'

/** The marks a signer adopts once and then stamps on each box. */
export interface Adoption {
  signature: SignatureValueDto
  initials: SignatureValueDto
}

interface AdoptSignatureFormProps {
  name: string
  email: string
  needsInitials: boolean
  onAdopt: (adopted: Adoption) => void
}

/**
 * The "Adopt your signature" dialog. Closing it without adopting (Escape, the
 * close button, or a click outside) leaves the signer on the document, where
 * they can still read it or decline; clicking a signature box opens it again.
 */
export function AdoptSignature({
  open,
  onOpenChange,
  ...form
}: AdoptSignatureFormProps & {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <AdoptSignatureForm {...form} />
      </DialogContent>
    </Dialog>
  )
}

/**
 * Lives inside the dialog content so closing it starts the form over: a drawn
 * signature exists only on the canvas, which doesn't survive the dialog closing.
 */
function AdoptSignatureForm({ name, email, needsInitials, onAdopt }: AdoptSignatureFormProps) {
  const draft = useAdoptionDraft(name, needsInitials)
  const { adoption } = draft

  return (
    <>
      <DialogHeader>
        <DialogTitle>Adopt your signature</DialogTitle>
        <DialogDescription>
          Confirm who you are, then create a signature. You will click each box on
          the document to apply it.
        </DialogDescription>
      </DialogHeader>
      <dl className="space-y-1 rounded-md border border-border bg-muted/40 p-3 text-sm">
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Name</dt>
          <dd className="font-medium text-foreground">{name}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">Email</dt>
          <dd className="font-medium text-foreground">{email}</dd>
        </div>
      </dl>
      <AdoptionPads draft={draft} />
      <DialogFooter>
        <Button
          disabled={!adoption}
          onClick={() => {
            if (adoption) {
              onAdopt(adoption)
            }
          }}
        >
          I confirm — adopt signature
        </Button>
      </DialogFooter>
    </>
  )
}

/** A signature, and initials when the signer needs them, being created. */
interface AdoptionDraft {
  signature: SignatureValueDto
  setSignature: (value: SignatureValueDto) => void
  initials: SignatureValueDto
  setInitials: (value: SignatureValueDto) => void
  needsInitials: boolean
  /** What adopting would adopt, or null while a needed mark is empty. */
  adoption: Adoption | null
}

/**
 * Starts the signature typed from the name the sender gave, and the initials
 * from that name; adopting is still the signer's own act.
 */
export function useAdoptionDraft(name: string, needsInitials: boolean): AdoptionDraft {
  const [signature, setSignature] = React.useState<SignatureValueDto>({
    kind: 'type',
    data: name,
  })
  const [initials, setInitials] = React.useState<SignatureValueDto>(() => ({
    kind: 'type',
    data: initialsFromName(name),
  }))

  const ready =
    fieldValuePresent(signature) && (!needsInitials || fieldValuePresent(initials))
  const adoption: Adoption | null = ready
    ? {
        signature,
        initials: needsInitials ? initials : { kind: 'type', data: initialsFromName(name) },
      }
    : null

  return { signature, setSignature, initials, setInitials, needsInitials, adoption }
}

/** The signature pad, and the initials pad when the signer needs one. */
export function AdoptionPads({ draft }: { draft: AdoptionDraft }) {
  return (
    <div className="space-y-4">
      <SignaturePad label="Signature" value={draft.signature} onChange={draft.setSignature} />
      {draft.needsInitials ? (
        <SignaturePad label="Initials" value={draft.initials} onChange={draft.setInitials} />
      ) : null}
    </div>
  )
}

function initialsFromName(name: string): string {
  return name
    .split(/\s+/)
    .filter((part) => part.length > 0)
    .map((part) => part.slice(0, 1))
    .join('')
    .slice(0, 3)
    .toUpperCase()
}
